/**
 * HDL Purchase Section 2 JavaScript
 * - Dynamic price calculation per card block
 * - Shopify AJAX bundle add-to-cart with cart drawer integration & freebie gift
 */

(function () {
  'use strict';

  function initHdlPurchase2Section(section) {
    if (!section || section.dataset.hdl2Initialized === 'true') return;
    section.dataset.hdl2Initialized = 'true';

    const cardElements = section.querySelectorAll('.hdl-purchase-2__card');

    cardElements.forEach((card) => {
      initCard(card, section);
    });

    initMobileSlider(section);
  }

  function initMobileSlider(section) {
    const sliderWrapper = section.querySelector('.hdl-purchase-2__slider-wrapper');
    if (!sliderWrapper) return;

    const cardsWrapper = sliderWrapper.querySelector('.hdl-purchase-2__cards-wrapper');
    const prevBtn = sliderWrapper.querySelector('.hdl-purchase-2__slider-arrow--prev');
    const nextBtn = sliderWrapper.querySelector('.hdl-purchase-2__slider-arrow--next');

    if (!cardsWrapper || !prevBtn || !nextBtn) return;

    function getCardScrollStep() {
      const firstCard = cardsWrapper.querySelector('.hdl-purchase-2__card');
      if (firstCard) {
        const style = window.getComputedStyle(cardsWrapper);
        const gap = parseFloat(style.columnGap || style.gap) || 16;
        return firstCard.offsetWidth + gap;
      }
      return 331;
    }

    function updateArrowStates() {
      const maxScroll = cardsWrapper.scrollWidth - cardsWrapper.clientWidth;
      const scrollLeft = cardsWrapper.scrollLeft;

      if (maxScroll <= 5) {
        prevBtn.classList.add('is-disabled');
        nextBtn.classList.add('is-disabled');
        return;
      }

      if (scrollLeft <= 10) {
        prevBtn.classList.add('is-disabled');
      } else {
        prevBtn.classList.remove('is-disabled');
      }

      if (scrollLeft >= maxScroll - 10) {
        nextBtn.classList.add('is-disabled');
      } else {
        nextBtn.classList.remove('is-disabled');
      }
    }

    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const step = getCardScrollStep();
      cardsWrapper.scrollBy({ left: -step, behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const step = getCardScrollStep();
      cardsWrapper.scrollBy({ left: step, behavior: 'smooth' });
    });

    let scrollRaf;
    cardsWrapper.addEventListener('scroll', function () {
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(updateArrowStates);
    }, { passive: true });

    window.addEventListener('resize', updateArrowStates);

    // Initial check after layout
    setTimeout(updateArrowStates, 100);
    updateArrowStates();
  }

  function initCard(card, section) {
    const discountPercentage = parseFloat(card.dataset.discountPercentage) || 15;
    const comparePriceEl = card.querySelector('.hdl-purchase-2__compare-price');
    const discountedPriceEl = card.querySelector('.hdl-purchase-2__discounted-price');
    const productItems = Array.from(card.querySelectorAll('.hdl-purchase-2__product-item[data-price]'));
    const addToCartBtn = card.querySelector('.hdl-purchase-2__btn');
    const errorMsgEl = card.querySelector('.hdl-purchase-2__error-message');

    // Dynamic price calculation
    function calculateAndRenderPricing() {
      if (card.dataset.hasManualPrice === 'true') return;
      if (productItems.length === 0) return;

      let totalOriginalCents = 0;
      let validCount = 0;

      productItems.forEach((item) => {
        const price = parseFloat(item.dataset.price);
        if (!isNaN(price) && price > 0) {
          totalOriginalCents += price;
          validCount++;
        }
      });

      if (validCount === 0) return;

      const discountedCents = Math.round(totalOriginalCents * (1 - discountPercentage / 100));

      if (comparePriceEl) {
        comparePriceEl.textContent = formatMoney(totalOriginalCents);
      }
      if (discountedPriceEl) {
        discountedPriceEl.textContent = formatMoney(discountedCents);
      }
    }

    function formatMoney(cents) {
      if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.moneyFormat || 'CHF {{amount}}');
      }

      const amount = (cents / 100).toFixed(2);
      const isClean = cents % 100 === 0;
      const formattedAmount = isClean ? `${cents / 100}.–` : amount;
      const currency = window.Shopify?.currency?.active || 'CHF';
      return `${currency} ${formattedAmount}`;
    }

    // Add to Cart handler for this collection card
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', async function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (addToCartBtn.classList.contains('loading')) return;

        const itemsToAdd = [];
        const isGerman = window.location.pathname.includes('/de') || document.documentElement.lang === 'de';
        const discountPropName = isGerman ? 'Glückwunsch!' : 'Congrats!';
        const discountPropVal = isGerman
          ? `${discountPercentage}% Rabatt angewendet`
          : `${discountPercentage}% discount applied`;
        const bundleId = card.dataset.blockId || 'hdl_collection';

        productItems.forEach((item) => {
          const variantId = item.dataset.variantId;
          if (variantId && Number(variantId)) {
            itemsToAdd.push({
              id: Number(variantId),
              quantity: 1,
              properties: {
                [discountPropName]: discountPropVal,
                _bundle: bundleId
              }
            });
          }
        });

        if (itemsToAdd.length === 0) {
          console.warn('HDL Purchase Section 2: No products available to add in this card.');
          return;
        }

        // Set Loading State
        if (errorMsgEl) errorMsgEl.style.display = 'none';
        addToCartBtn.classList.add('loading');
        addToCartBtn.setAttribute('aria-disabled', 'true');
        if (typeof showCartGlobalLoader === 'function') showCartGlobalLoader();

        const cartDrawer = document.querySelector('cart-drawer');
        let sectionsArr = [];
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          sectionsArr = cartDrawer.getSectionsToRender().map((s) => s.id);
        }

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: itemsToAdd,
              sections: sectionsArr,
              sections_url: window.location.pathname
            })
          }).then((res) => res.json());

          // Handle server errors
          if (response.status) {
            console.error('HDL Purchase 2 Add to Cart Error:', response.description || response.message);
            if (errorMsgEl) {
              errorMsgEl.textContent = response.description || response.message || 'Error adding products to cart';
              errorMsgEl.style.display = 'block';
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'hdl-purchase-section-2',
                errors: response.description,
                message: response.message
              });
            }
            return;
          }

          // Trigger Theme Cart Events
          if (typeof $ !== 'undefined') {
            $(document).trigger('product-form-responsed', [response]);
          }
          document.dispatchEvent(new Event('product-form-responsed'));

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'hdl-purchase-section-2',
              response: response
            });
          }

          // Render & Open Cart Drawer
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(response);
          } else if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }

          // Animate Progress Bar if available
          if (typeof animateCartDrawerProgressBar === 'function') {
            animateCartDrawerProgressBar();
          }

          // Set order note language tag (DE / EN)
          const orderNote = isGerman ? 'DE' : 'EN';
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: orderNote })
          });

        } catch (err) {
          console.error('HDL Purchase 2 Exception:', err);
          if (errorMsgEl) {
            errorMsgEl.textContent = 'Could not add items to cart. Please try again.';
            errorMsgEl.style.display = 'block';
          }
        } finally {
          addToCartBtn.classList.remove('loading');
          addToCartBtn.removeAttribute('aria-disabled');
          if (typeof hideCartGlobalLoader === 'function') hideCartGlobalLoader();
        }
      });
    }

    // Initial pricing calculation
    calculateAndRenderPricing();
  }

  function initAllHdl2Sections() {
    document.querySelectorAll('.hdl-purchase-2').forEach(initHdlPurchase2Section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllHdl2Sections);
  } else {
    initAllHdl2Sections();
  }

  // Shopify Theme Editor Integration
  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector('.hdl-purchase-2');
    if (section) initHdlPurchase2Section(section);
  });
})();

